import {
  AlignmentType,
  ExternalHyperlink,
  ImageRun,
  Paragraph,
  TextRun,
} from 'docx';

// Utils
import { formatDateStringToMonthYear } from '@shared/utils';
import { svgToImageBuffer } from '@/components/template/resume/utils/image';
import { CONTACT_ICON_SVGS } from '@/components/template/resume/utils/icons';
import { bodyRun, buildIconPrefix, dividerParagraph } from '../../utils';

// Constants
import {
  AVATAR_SIZE_PX,
  FONT_FAMILY,
  FONT_SIZES,
  ICON_SIZE,
  COLORS,
  SPACINGS,
} from '../../constants';

export interface ContactIconBuffers {
  birthday: Uint8Array | null;
  phone: Uint8Array | null;
  email: Uint8Array | null;
  location: Uint8Array | null;
  link: Uint8Array | null;
}

export type ContactInfo = {
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  birthday?: string | null;
};

/** Loads all contact icon SVGs as PNG buffers in parallel */
export const loadContactIcons = async (): Promise<ContactIconBuffers> => {
  const [birthday, phone, email, location, link] = await Promise.all([
    svgToImageBuffer(CONTACT_ICON_SVGS.birthday, ICON_SIZE, ICON_SIZE),
    svgToImageBuffer(CONTACT_ICON_SVGS.phone, ICON_SIZE, ICON_SIZE),
    svgToImageBuffer(CONTACT_ICON_SVGS.email, ICON_SIZE, ICON_SIZE),
    svgToImageBuffer(CONTACT_ICON_SVGS.location, ICON_SIZE, ICON_SIZE),
    svgToImageBuffer(CONTACT_ICON_SVGS.link, ICON_SIZE, ICON_SIZE),
  ]);

  return { birthday, phone, email, location, link };
};

/** Creates a contact row with icon and optional hyperlink */
const ContactRow = (
  iconData: Uint8Array | null,
  label: string,
  text: string,
  href?: string,
): Paragraph => {
  const linkRun = href
    ? new ExternalHyperlink({
        link: href,
        children: [bodyRun(text, { size: FONT_SIZES.lg })],
      })
    : bodyRun(text, { size: FONT_SIZES.lg });

  return new Paragraph({
    spacing: { before: SPACINGS.medium },
    children: [...buildIconPrefix(iconData, label), linkRun],
  });
};

/** Creates the sidebar personal info block with avatar, name, title and contacts */
export const PersonalInfoSection = (
  name: string,
  title: string,
  avatarData: Uint8Array | null,
  info: ContactInfo,
  icons: ContactIconBuffers,
): Paragraph[] => {
  const paragraphs: Paragraph[] = [];

  if (avatarData) {
    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: SPACINGS.large },
        children: [
          new ImageRun({
            data: avatarData,
            transformation: {
              width: AVATAR_SIZE_PX,
              height: AVATAR_SIZE_PX,
            },
            type: 'png',
            altText: {
              title: 'Avatar',
              description: 'Profile photo',
              name: 'avatar',
            },
          }),
        ],
      }),
    );
  }

  if (name) {
    // Break name after first space to match PDF wrapText behavior
    const nameRunBase = {
      font: FONT_FAMILY,
      size: FONT_SIZES['6xl'],
      bold: true,
      allCaps: true,
      color: COLORS.foreground,
    } as const;

    const spaceIdx = name.indexOf(' ');
    const nameRuns =
      spaceIdx > 0
        ? [
            new TextRun({ ...nameRunBase, text: name.slice(0, spaceIdx) }),
            new TextRun({
              ...nameRunBase,
              text: name.slice(spaceIdx + 1),
              break: 1,
            }),
          ]
        : [new TextRun({ ...nameRunBase, text: name })];

    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: SPACINGS.medium },
        children: nameRuns,
      }),
    );
  }

  if (title) {
    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: title,
            font: FONT_FAMILY,
            size: FONT_SIZES['4xl'],
            color: COLORS.primary,
          }),
        ],
      }),
    );
  }

  const { phone, email, address, birthday } = info;
  if (!phone && !email && !address && !birthday) return paragraphs;

  paragraphs.push(dividerParagraph());

  if (birthday) {
    paragraphs.push(
      ContactRow(
        icons.birthday,
        'Birthday',
        formatDateStringToMonthYear(birthday),
      ),
    );
  }

  if (phone) {
    paragraphs.push(ContactRow(icons.phone, 'Phone', phone, `tel:${phone}`));
  }

  if (email) {
    paragraphs.push(ContactRow(icons.email, 'Email', email, `mailto:${email}`));
  }

  if (address) paragraphs.push(ContactRow(icons.location, 'Location', address));

  return paragraphs;
};
